/* Lightweight functional programming utilities */

/**
 * Takes a function `f` and returns a function `g` such that if called with the
 * same arguments when `f` returns a "truthy" value, `g` returns `false` and
 * when `f` returns a "falsy" value `g` returns `true`.
 *
 * @param {Function} f
 * @return {Function}
 * @example
 *      const isNil = (v) => v === null;
 *      const isNotNil = complement(isNil)
 *      isNil(null); //=> true
 *      isNotNil(null); //=> false
 *      isNil(7); //=> false
 *      isNotNil(7); //=> true
 */
const complement = (fn) => (...args) => !fn.apply(this, args);

/**
 * Creates a function which is a composition of the passed functions. Each
 * function consumes the return value of the function that follows.
 * Composing functions `f()`, `g()`, and `h()` would produce the result
 * of `f(g(h()))`.
 *
 * @name compose
 * @param  {...Function} fns Functions to compose
 * @returns {Function} Function that is the composed `functions`
 * @example
 * const add1 = (n) => n + 1;
 * const mul2 = (n) => n * 2;
 *
 * const add1AndMul2 = compose(mul2, add1);
 * add1AndMul2(4) // = 10
 *
 * const mul2AndAdd1 = compose(add1, mul2);
 * mul2AndAdd1(4) // = 9
 */
const compose = (...fns) => (x) => fns.reduceRight((v, f) => f(v), x);

/**
 * Creates a function which is a composition of the passed asynchronous
 * functions. Each function consumes the value of the promise that returns the
 * function that follows. Composing functions `f()`, `g()`, and `h()` would
 * produce the result of `h.then(g).then(f)`.
 *
 * @name composeAsync
 * @param  {...Function} fns Functions to compose
 * @returns {Function} Function that is the composed `functions`
 * @example
 * const add1 = (n) => n + 1;
 * const mul2 = (n) => n * 2;
 *
 * const add1AndMul2 = compose(mul2, add1);
 * add1AndMul2(4).then(c => c) // c = 10
 */
const composeAsync = (...fns) => (x) => fns.reduceRight((v, f) => v.then(f), Promise.resolve(x));

/**
 * A function that does nothing but return the parameter supplied to it. Good
 * as a default or placeholder function.
 * @param {any} x Any value
 * @returns {any} Same x
 */
const identity = (x) => x;

/**
 * Creates a function which a composition of the passed functions, but in a
 * reverse order than `compose()`. The params can be read as a sequence of
 * events.
 *
 * @name pipe
 * @param  {...Function} fns Functions as a sequence
 * @returns {Function} Function that is the composed `functions`
 */
const pipe = (...fns) => (x) => fns.reduce((v, f) => f(v), x);

/**
 * Creates a function which a composition of the passed asynchronous functions,
 * but in a reverse order than `composeAsync()`.
 *
 * @name pipeAsync
 * @param  {...Function} fns Functions as a sequence
 * @returns {Function} Function that is the composed `functions`
 */
const pipeAsync = (...fns) => (x) => fns.reduce((v, f) => v.then(f), Promise.resolve(x));

export {
  complement,
  compose,
  composeAsync,
  identity,
  pipe,
  pipeAsync,
};
